import { useEffect, useMemo, useRef, useState } from "react";
import { availabilityDetail, moneyShort, summarizeUnits } from "./unitStats";
import AvailabilityPopover from "./AvailabilityPopover";
import "./portfolioTable.css";

const OPEN_DELAY = 150;
const CLOSE_DELAY = 200;

/**
 * Open-on-hover, stay-open-on-click for the Vacant / Already Rented tiles. Hover opens
 * after a short pause (so sweeping the mouse across the row does not flash popovers);
 * a click pins it until the next click, Escape, or a click elsewhere.
 */
function useTilePopover() {
  const [open, setOpen] = useState(null);
  const [pinned, setPinned] = useState(false);
  const timer = useRef(null);
  const rootRef = useRef(null);

  const later = (fn, ms) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(fn, ms);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(null);
        setPinned(false);
      }
    };
    const onDown = (e) => {
      if (pinned && rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(null);
        setPinned(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, pinned]);

  const tileProps = (kind) => ({
    role: "button",
    tabIndex: 0,
    "aria-expanded": open === kind,
    "aria-haspopup": "dialog",
    onMouseEnter: () => {
      if (pinned) return;
      later(() => setOpen(kind), open ? 0 : OPEN_DELAY);
    },
    onMouseLeave: () => {
      if (!pinned) later(() => setOpen(null), CLOSE_DELAY);
    },
    onClick: (e) => {
      // Clicks inside the open popover (scrolling, selecting text) never close it.
      if (e.target.closest(".avp")) return;
      clearTimeout(timer.current);
      if (pinned && open === kind) {
        setPinned(false);
        setOpen(null);
      } else {
        setPinned(true);
        setOpen(kind);
      }
    },
    onKeyDown: (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      const closing = open === kind;
      setPinned(!closing);
      setOpen(closing ? null : kind);
    },
  });

  return { open, pinned, rootRef, tileProps };
}

/**
 * The summary tiles every mastersheet view shows above its table: units,
 * occupied, vacant by the end of next month, already rented (All Properties only —
 * it needs each row's `signed_lease` flag), basic rent, target rent, gap to target.
 * Hovering Vacant or Already Rented opens the unit-by-unit detail; a click pins it. `rows` is what is
 * currently visible; `totalRows` is the unfiltered count so the Units tile can
 * read "12 of 345 units" while a filter is narrowing the view.
 */
const MastersheetStats = ({ rows, totalRows, propertyCount, className = "", referenceDate }) => {
  // `referenceDate` (Historic sheet): the next-month window counts from that day, not today.
  const t = useMemo(() => summarizeUnits(rows, referenceDate), [rows, referenceDate]);
  const total = totalRows ?? rows.length;
  const pop = useTilePopover();
  // Built only while a popover is open: the lists cost nothing until someone asks.
  const detail = useMemo(() => (pop.open ? availabilityDetail(rows, referenceDate) : null), [pop.open, rows, referenceDate]);
  const popover = (kind) =>
    pop.open === kind && detail ? <AvailabilityPopover kind={kind} detail={detail} stats={t} hasRentedInfo={t.hasRentedInfo} /> : null;

  let unitsLabel;
  if (rows.length !== total) unitsLabel = `${rows.length} of ${total} units`;
  else if (propertyCount !== undefined && propertyCount !== null) {
    unitsLabel = `${total} units · ${propertyCount} propert${propertyCount === 1 ? "y" : "ies"}`;
  } else unitsLabel = `${total} units`;

  return (
    <div className={`pft-stats ${className}`.trim()} data-testid="mastersheet-stats" ref={pop.rootRef}>
      <div className="pft-stat">
        <div className="pft-stat__label">Units</div>
        <div className="pft-stat__value">{unitsLabel}</div>
      </div>
      <div className="pft-stat pft-stat--occupied">
        <div className="pft-stat__label">Occupied</div>
        <div className="pft-stat__value">{t.occupied}</div>
        <div className="pft-stat__sub">{t.occupancy}% occupancy</div>
      </div>
      {/* Vacant = empty today + leases ending by the end of next month (owner, 2026-09-28). */}
      <div
        className={`pft-stat pft-stat--vacant pft-stat--pop ${pop.open === "vacant" ? "is-open" : ""}`}
        data-testid="stat-vacant"
        {...pop.tileProps("vacant")}
      >
        <div className="pft-stat__label">Vacant by {t.window.endLabel}</div>
        <div className="pft-stat__value">{t.goingVacant}</div>
        <div className="pft-stat__sub">
          {t.vacant} now · {t.expiring} lease{t.expiring === 1 ? "" : "s"} ending
        </div>
        {popover("vacant")}
      </div>
      {t.hasRentedInfo && (
        <div
          className={`pft-stat pft-stat--rented pft-stat--pop ${pop.open === "rented" ? "is-open" : ""}`}
          data-testid="stat-already-rented"
          {...pop.tileProps("rented")}
        >
          <div className="pft-stat__label">Already Rented</div>
          <div className="pft-stat__value">{t.rented}</div>
          <div className="pft-stat__sub">
            {t.rentedVacant} of {t.vacant} vacant · {t.rentedExpiring} of {t.expiring} ending
          </div>
          {popover("rented")}
        </div>
      )}
      <div className="pft-stat pft-stat--rent">
        <div className="pft-stat__label">Basic Rent</div>
        <div className="pft-stat__value">{moneyShort(t.basic)}</div>
        <div className="pft-stat__sub">per month</div>
      </div>
      <div className="pft-stat">
        <div className="pft-stat__label">Target Rent</div>
        <div className="pft-stat__value">{moneyShort(t.target)}</div>
        <div className="pft-stat__sub">per month</div>
      </div>
      <div className="pft-stat pft-stat--gap">
        <div className="pft-stat__label">Gap to Target</div>
        <div className="pft-stat__value">{moneyShort(t.gap)}</div>
        <div className="pft-stat__sub">▲{t.up} ~{t.near} ▼{t.down}</div>
      </div>
    </div>
  );
};

export default MastersheetStats;
